import React, { useState } from 'react';

import _, { isEmpty, isNil } from 'lodash';
import { DateTime } from 'luxon';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import type { RenewalRelationship } from '@appTypes/esb';
import { getMYSTUrl } from '@config/config';
import { useGetPersonRenewalRelationshipsQuery } from '@modules/rtk/esb-api';
import { Modal, S, T } from '@modules/shared/components';
import {
  loginDataInitializedSel,
  personGuidSel,
  userDataInitializedSel,
} from '@modules/user/duck';
import { currentUserAgeSel } from '@modules/user/duck/selectorsTyped';
import { ExpirationStatus } from '@modules/userNotifications/enums';
import {
  filterRenewalNotifications,
  getExpirationStatus,
  getMembershipExpirationDismissFlag,
  isParentRelationship,
  storeMembershipExpirationDismissFlag,
} from '@modules/userNotifications/utils';

function getExpirationMessage(params: {
  relationship: RenewalRelationship;
  isParent: boolean;
  personName?: string;
}) {
  const status = getExpirationStatus(params.relationship);

  const messages = {
    [ExpirationStatus.ABOUT_TO_EXPIRE]: 'aboutToExpire',
    [ExpirationStatus.EXPIRED]: 'expired',
    [ExpirationStatus.LONG_EXPIRED]: 'longExpired',
    [ExpirationStatus.UNKNOWN]: null,
  };

  const messageTag = messages[status];

  const messageId = `userNotifications.modals.membershipExpiration.${
    params.isParent ? 'parentGuardian' : 'leader'
  }.message.${messageTag}`;

  return (
    <FormattedMessage
      id={messageId}
      values={{
        personName: params.personName,
        link: (
          <a href="#">
            <FormattedMessage id="userNotifications.modals.membershipExpiration.renew" />
          </a>
        ),
        br: <br />,
        date: (
          <strong>
            {DateTime.fromISO(params.relationship.personExpiryDt).toFormat(
              'LLLL d, yyyy',
            )}
          </strong>
        ),
      }}
    />
  );
}

function ExpirationModal(props: { renewals: RenewalRelationship[] }) {
  const { renewals } = props;
  const [isVisible, setIsVisible] = useState(true);

  const visibleRenewals = renewals
    .filter(renewal => {
      const hasSeenBefore = getMembershipExpirationDismissFlag({
        personGuid: renewal.personGuid,
        organizationGuid: renewal.organizationGuid,
        registrationId: renewal.registrationId,
      });

      return (
        !hasSeenBefore &&
        getExpirationStatus(renewal) !== ExpirationStatus.UNKNOWN
      );
    })
    .map(renewal => ({
      renewal,
      isParent: isParentRelationship(renewal),
      message: getExpirationMessage({
        relationship: renewal,
        isParent: isParentRelationship(renewal),
        personName: [renewal.firstName, renewal.middleName, renewal.lastName]
          .filter(Boolean)
          .join(' '),
      }),
    }));

  if (visibleRenewals.length === 0) {
    return null;
  }

  const hasParent = visibleRenewals.some(r => r.isParent);

  const title = (
    <FormattedMessage
      id={
        hasParent
          ? 'userNotifications.modals.membershipExpiration.parentGuardian.title'
          : 'userNotifications.modals.membershipExpiration.leader.title'
      }
    />
  );

  const onRenew = () => {
    window?.open(`${getMYSTUrl()}/tools/my-applications`, '_blank');
    setIsVisible(false);
  };

  return (
    <Modal
      fullscreenMobile={false}
      useNewStyle
      closable={false}
      destroyOnClose
      visible={isVisible}
      okText={
        <FormattedMessage id="userNotifications.modals.membershipExpiration.renew" />
      }
      onCancel={() => {
        setIsVisible(false);

        visibleRenewals.forEach(({ renewal }) => {
          storeMembershipExpirationDismissFlag({
            personGuid: renewal.personGuid,
            organizationGuid: renewal.organizationGuid,
            registrationId: renewal.registrationId,
          });
        });
      }}
      onOk={onRenew}
      useDefaultFooter
      title={
        <T colored size="3">
          {title}
        </T>
      }
    >
      {visibleRenewals.map(({ renewal, message }) => (
        <p
          key={`${renewal.personGuid}-${renewal.positionId}-${renewal.registrationId}`}
        >
          <S>{message}</S>
        </p>
      ))}
    </Modal>
  );
}

export function MembershipExpirationModal(props: {
  renewals: RenewalRelationship[];
  currentUserPersonGuid: string;
}) {
  const { renewals } = props;
  if (isNil(renewals) || isEmpty(renewals)) return null;

  const selfRenewal = _.chain(renewals)
    .filter(renewal => renewal.personGuid === props.currentUserPersonGuid)
    .sortBy('organizationExpiryDt')
    .first()
    .value();

  const youthRenewals = _.chain(renewals)
    .filter(renewal => isParentRelationship(renewal))
    .filter(Boolean)
    .value();

  const validRenewals = [selfRenewal, ...youthRenewals].filter(Boolean);
  return (
    <ErrorBoundary FallbackComponent={() => <div />}>
      <ExpirationModal renewals={validRenewals} />
    </ErrorBoundary>
  );
}

export function MembershipExpirationModalContainer() {
  const userDataInitialized = useSelector(userDataInitializedSel);
  const loginDataInitialized = useSelector(loginDataInitializedSel);
  const personGuid = useSelector(personGuidSel);
  const currentUserAge = useSelector(currentUserAgeSel);

  const skip = !userDataInitialized || !loginDataInitialized || !personGuid;

  const { data: renewals, isUninitialized } =
    useGetPersonRenewalRelationshipsQuery(
      {
        personGuid: personGuid as string,
      },
      {
        skip,
        selectFromResult: result => {
          const data = filterRenewalNotifications(result.data).filter(item => {
            // SBL-5687 filter out if youth is under 18
            if (item.personGuid === personGuid && currentUserAge < 18) {
              return false;
            }

            return true;
          });
          return { ...result, data };
        },
      },
    );

  const emptyRenewals = _.isEmpty(renewals);

  const shouldHide = skip || isUninitialized || emptyRenewals;

  if (shouldHide || !renewals) return null;

  return (
    <MembershipExpirationModal
      currentUserPersonGuid={personGuid}
      renewals={renewals}
    />
  );
}

export default MembershipExpirationModalContainer;
